import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ExternalLink, Building2, ArrowRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import AmbientBackground from '../common/AmbientBackground';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Standard OSM tiles - always light, Carto tiles need an api key
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

const createGolfIcon = () => L.divIcon({
  className: 'custom-golf-marker',
  html: `
    <div style="
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, #34d399 0%, #059669 100%);
      border: 3px solid #FFFFFF;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.25), 0 0 0 4px rgba(52, 211, 153, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
    ">
      <div style="
        transform: rotate(45deg);
        font-size: 18px;
        filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3));
      ">⛳</div>
    </div>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

const FitBounds = ({ clubs }) => {
  const map = useMap();
  useEffect(() => {
    if (clubs.length > 0) {
      const bounds = L.latLngBounds(clubs.map(c => [c.latitude, c.longitude]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [clubs, map]);
  return null;
};

const ClubsMap = () => {
  const { isDarkMode } = useTheme();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const golfIcon = createGolfIcon();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          'https://api.fsgu.co.za/get-clubs.php?active=true&with_coordinates=true'
        );
        const data = await res.json();
        if (mounted && data.success && data.data) {
          const valid = data.data.filter(
            c => c.latitude != null && c.longitude != null
          );
          setClubs(valid);
        }
      } catch (err) {
        console.error('Clubs map load error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const fallbackCenter = [-28.5, 26.5];

  return (
    <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      <AmbientBackground variant="green" />

      <div className="container-custom relative">
        {/* Centered header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
            <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            }`}>
              The province
            </span>
            <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
          </div>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-cursive mb-3 ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}>
            Find a club near you
          </h2>
          <p className={`text-base sm:text-lg mb-6 ${
            isDarkMode ? 'text-slate-400' : 'text-slate-600'
          }`}>
            Explore all {clubs.length} affiliated golf clubs in the Free State
          </p>
          <Link
            to="/clubs"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold backdrop-blur-xl border transition-all duration-300 hover:scale-[1.02] ${
              isDarkMode
                ? 'bg-white/[0.05] border-white/10 text-emerald-400 hover:bg-white/[0.08]'
                : 'bg-white/60 border-white/80 text-emerald-600 hover:bg-white/80'
            }`}
          >
            <Building2 className="w-4 h-4" />
            View full directory
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-6xl mx-auto"
        >
          {/* Glow behind map */}
          <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-br from-emerald-500/20 via-amber-500/10 to-emerald-600/20 blur-2xl opacity-60" />

          {/* Glass frame */}
          <div className={`relative rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl border h-[420px] sm:h-[500px] lg:h-[580px] ${
            isDarkMode
              ? 'bg-white/[0.04] border-white/[0.1]'
              : 'bg-white/60 border-white/80 shadow-2xl shadow-emerald-900/10'
          }`}>
            <div
              className="absolute inset-x-0 top-0 h-px z-[500] pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)' }}
            />

            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className={`inline-block animate-spin rounded-full h-10 w-10 border-4 border-solid border-current border-r-transparent ${
                  isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
                }`} />
              </div>
            ) : (
              <>
                <MapContainer
                  center={fallbackCenter}
                  zoom={7}
                  scrollWheelZoom={false}
                  style={{ width: '100%', height: '100%' }}
                  ref={mapRef}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url={TILE_URL}
                    maxZoom={19}
                  />
                  <FitBounds clubs={clubs} />

                  {clubs.map((club) => (
                    <Marker
                      key={club.id}
                      position={[club.latitude, club.longitude]}
                      icon={golfIcon}
                    >
                      <Popup className="golf-club-popup">
                        <div className="p-2" style={{ minWidth: '260px', maxWidth: '300px' }}>
                          <h3 className="font-bold text-base mb-3 pr-4" style={{ color: '#1C1C1E', lineHeight: '1.4' }}>
                            {club.name}
                          </h3>

                          {club.address && (
                            <div className="flex items-start space-x-2 mb-2.5">
                              <MapPin style={{ width: '15px', height: '15px', color: '#10b981', flexShrink: 0, marginTop: '2px' }} />
                              <div style={{ fontSize: '13px', color: '#6D6D70', lineHeight: '1.5' }}>
                                <p style={{ color: '#515154', marginBottom: '2px', fontWeight: 500 }}>
                                  {club.address}
                                </p>
                                {club.city && (
                                  <p style={{ fontSize: '12px' }}>
                                    {club.city}{club.postal_code ? `, ${club.postal_code}` : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {club.phone && (
                            <div className="flex items-center space-x-2 mb-2.5">
                              <Phone style={{ width: '15px', height: '15px', color: '#10b981', flexShrink: 0 }} />
                              <a href={`tel:${club.phone}`} style={{ fontSize: '13px', color: '#10b981', textDecoration: 'none', fontWeight: 500 }}>
                                {club.phone}
                              </a>
                            </div>
                          )}

                          {club.email && (
                            <div className="flex items-center space-x-2 mb-2.5">
                              <Mail style={{ width: '15px', height: '15px', color: '#10b981', flexShrink: 0 }} />
                              <a href={`mailto:${club.email}`} style={{ fontSize: '13px', color: '#10b981', textDecoration: 'none', fontWeight: 500, wordBreak: 'break-all' }}>
                                {club.email}
                              </a>
                            </div>
                          )}

                          {club.website && (
                            <div className="flex items-center space-x-2 mb-2.5">
                              <ExternalLink style={{ width: '15px', height: '15px', color: '#10b981', flexShrink: 0 }} />
                              <a href={club.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#10b981', textDecoration: 'none', fontWeight: 500 }}>
                                Visit Website
                              </a>
                            </div>
                          )}

                          {(club.holes || club.par) && (
                            <div className="mt-3 pt-3 flex items-center justify-between" style={{
                              borderTop: '1px solid #E5E5EA',
                              fontSize: '12px',
                              color: '#6D6D70'
                            }}>
                              {club.holes && (
                                <span><span style={{ marginRight: '4px' }}>⛳</span>{club.holes} Holes</span>
                              )}
                              {club.par && (
                                <span style={{ fontWeight: 500, color: '#515154' }}>Par {club.par}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>

                {/* Floating glass stats pill */}
                <div className="absolute top-4 left-4 z-[400] px-4 py-2.5 rounded-full backdrop-blur-2xl border shadow-lg bg-white/70 border-white/90 text-slate-900">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="text-xs sm:text-sm font-semibold">
                      {clubs.length} clubs mapped
                    </span>
                  </div>
                </div>
              </>
            )}

            <style>{`
              .leaflet-container {
                background: #f1f5f9;
                font-family: -apple-system, BlinkMacSystemFont, 'Avenir Next', 'Avenir', 'Segoe UI', sans-serif;
              }

              .leaflet-popup-content-wrapper {
                background: rgba(255, 255, 255, 0.98);
                backdrop-filter: blur(20px);
                border-radius: 20px;
                padding: 0;
                box-shadow: 0 20px 60px -15px rgba(0, 0, 0, 0.4);
                border: 1px solid rgba(0, 0, 0, 0.06);
              }

              .leaflet-popup-content {
                margin: 14px;
                font-size: 14px;
                line-height: 1.6;
                color: #1C1C1E;
              }

              .leaflet-popup-tip {
                background: rgba(255, 255, 255, 0.98);
                border: 1px solid rgba(0, 0, 0, 0.06);
              }

              .leaflet-popup-close-button {
                font-size: 20px;
                padding: 4px 8px !important;
                color: #64748b !important;
                font-weight: 300;
                top: 8px !important;
                right: 8px !important;
                width: 24px !important;
                height: 24px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                border-radius: 6px;
              }

              .leaflet-popup-close-button:hover {
                color: #10b981 !important;
                background: rgba(16, 185, 129, 0.1) !important;
              }

              .custom-golf-marker {
                background: transparent;
                border: none;
                transition: transform 0.2s ease;
              }

              .custom-golf-marker:hover {
                transform: scale(1.15);
              }

              .leaflet-control-zoom {
                border: none !important;
                border-radius: 12px !important;
                overflow: hidden;
                box-shadow: 0 4px 20px -5px rgba(0, 0, 0, 0.15);
                backdrop-filter: blur(20px);
              }

              .leaflet-control-zoom a {
                background: rgba(255, 255, 255, 0.9) !important;
                color: #1C1C1E !important;
                border: 1px solid rgba(0, 0, 0, 0.05) !important;
                width: 36px !important;
                height: 36px !important;
                line-height: 36px !important;
                font-size: 20px !important;
                transition: all 0.2s ease !important;
                backdrop-filter: blur(20px);
              }

              .leaflet-control-zoom a:hover {
                background: #10b981 !important;
                color: #FFFFFF !important;
              }

              .leaflet-control-zoom a:first-child {
                border-radius: 12px 12px 0 0 !important;
              }

              .leaflet-control-zoom a:last-child {
                border-radius: 0 0 12px 12px !important;
              }

              .leaflet-control-attribution {
                background: rgba(255, 255, 255, 0.7) !important;
                color: #64748b !important;
                font-size: 10px;
                padding: 4px 8px;
                border-radius: 8px 0 0 0;
                backdrop-filter: blur(10px);
              }

              .leaflet-control-attribution a {
                color: #10b981 !important;
              }
            `}</style>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClubsMap;